<template>
  <div class="docs-container">
    <a-page-header :title="config?.title || '系统文档'" :sub-title="config?.description">
      <template #extra>
        <a-input-search
          v-model:value="searchQuery"
          :placeholder="config?.search?.placeholder || '搜索文档...'"
          style="width: 320px"
          @search="handleSearch"
          allow-clear
          :loading="searching"
        />
      </template>
    </a-page-header>

    <!-- 搜索结果 -->
    <div v-if="searchMode" class="search-results">
      <a-card :bordered="false">
        <template #title>
          <span>搜索结果</span>
          <a-tag color="blue" style="margin-left: 8px">{{ searchResults.length }} 条</a-tag>
        </template>
        <template #extra>
          <a-button type="link" @click="clearSearch">
            <template #icon><CloseOutlined /></template>
            返回分类
          </a-button>
        </template>
        
        <a-spin :spinning="searching">
          <a-list
            :data-source="searchResults"
            item-layout="vertical"
          >
            <template #renderItem="{ item }">
              <a-list-item>
                <a-list-item-meta>
                  <template #title>
                    <router-link :to="`/docs/${item.category}/${item.file}`" class="search-result-title">
                      <span v-html="highlightText(item.title)"></span>
                    </router-link>
                    <a-tag color="blue" size="small" style="margin-left: 8px">{{ item.categoryName }}</a-tag>
                  </template>
                  <template #description>
                    <div class="search-snippet" v-html="highlightText(item.snippet || item.content?.substring(0, 200) + '...')"></div>
                  </template>
                </a-list-item-meta>
              </a-list-item>
            </template>
            <template #empty>
              <a-empty description="未找到匹配的文档" />
            </template>
          </a-list>
        </a-spin>
      </a-card>
    </div>

    <!-- 分类列表 -->
    <div v-else class="category-grid">
      <a-spin :spinning="loading">
        <a-row :gutter="[16, 16]">
          <a-col :xs="24" :sm="12" :lg="8" v-for="cat in config?.categories" :key="cat.key">
            <a-card hoverable class="category-card" @click="goToCategory(cat.key)">
              <template #title>
                <div class="category-title">
                  <component :is="getIcon(cat.icon)" class="category-icon" />
                  <span>{{ cat.name }}</span>
                </div>
              </template>
              <p class="category-desc">{{ cat.description }}</p>
              <template #actions>
                <span class="category-action">查看文档 <RightOutlined /></span>
              </template>
            </a-card>
          </a-col>
        </a-row>
      </a-spin>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import request from '@/api/request';
import Fuse from 'fuse.js';
import {
  RobotOutlined,
  BookOutlined,
  EditOutlined,
  ApiOutlined,
  CodeOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  RightOutlined,
  CloseOutlined,
  SearchOutlined
} from '@ant-design/icons-vue';

export default {
  name: 'DocsIndex',
  components: {
    RobotOutlined,
    BookOutlined,
    EditOutlined,
    ApiOutlined,
    CodeOutlined,
    ExperimentOutlined,
    FileTextOutlined,
    RightOutlined,
    CloseOutlined,
    SearchOutlined
  },
  setup() {
    const router = useRouter();
    const config = ref(null);
    const loading = ref(false);
    const searchQuery = ref('');
    const searchMode = ref(false);
    const searching = ref(false);
    const searchResults = ref([]);
    const searchIndex = ref([]);
    const fuse = ref(null);

    // 图标映射
    const iconMap = {
      RobotOutlined,
      BookOutlined,
      EditOutlined,
      ApiOutlined,
      CodeOutlined,
      ExperimentOutlined,
      FileTextOutlined
    };

    const getIcon = (iconName) => iconMap[iconName] || FileTextOutlined;

    // 加载配置
    const loadConfig = async () => {
      loading.value = true;
      try {
        const res = await request.get('/docs/config');
        if (res.code === 200) {
          config.value = res.data;
        }
      } catch (error) {
        console.error('加载文档配置失败:', error);
      } finally {
        loading.value = false;
      }
    };

    // 加载搜索索引
    const loadSearchIndex = async () => {
      try {
        const res = await request.get('/docs/search-index');
        if (res.code === 200) {
          searchIndex.value = res.data.index;
          
          // 初始化 Fuse.js
          fuse.value = new Fuse(searchIndex.value, {
            keys: [
              { name: 'title', weight: 0.4 },
              { name: 'keywords', weight: 0.3 },
              { name: 'content', weight: 0.2 },
              { name: 'categoryName', weight: 0.1 }
            ],
            threshold: 0.3,
            distance: 100,
            minMatchCharLength: 2,
            includeScore: true,
            includeMatches: true,
            useExtendedSearch: false,
            ignoreLocation: true,
          });
          
          console.log(`[Docs] 搜索索引加载完成，共 ${searchIndex.value.length} 篇文档`);
        }
      } catch (error) {
        console.error('加载搜索索引失败:', error);
      }
    };

    // 搜索
    const handleSearch = () => {
      const query = searchQuery.value.trim();
      
      if (!query || query.length < 2) {
        clearSearch();
        return;
      }
      
      searchMode.value = true;
      searching.value = true;
      
      try {
        if (fuse.value) {
          // 使用 Fuse.js 本地搜索
          const results = fuse.value.search(query, { limit: 30 });
          searchResults.value = results.map(result => ({
            ...result.item,
            score: result.score,
            snippet: generateSnippet(result.item.content, query),
          }));
        } else {
          // 降级到简单搜索
          const lowerQuery = query.toLowerCase();
          searchResults.value = searchIndex.value.filter(doc => 
            doc.title.toLowerCase().includes(lowerQuery) ||
            doc.keywords.toLowerCase().includes(lowerQuery) ||
            doc.content.toLowerCase().includes(lowerQuery)
          ).map(doc => ({
            ...doc,
            snippet: generateSnippet(doc.content, query),
          }));
        }
      } catch (error) {
        console.error('搜索失败:', error);
        searchResults.value = [];
      } finally {
        searching.value = false;
      }
    };

    // 生成搜索片段
    const generateSnippet = (content, query) => {
      if (!content || !query) return '';
      
      const lowerContent = content.toLowerCase();
      const lowerQuery = query.toLowerCase();
      const index = lowerContent.indexOf(lowerQuery);
      
      if (index === -1) {
        return content.substring(0, 150) + '...';
      }
      
      const start = Math.max(0, index - 60);
      const end = Math.min(content.length, index + query.length + 100);
      let snippet = content.substring(start, end);
      
      if (start > 0) snippet = '...' + snippet;
      if (end < content.length) snippet += '...';
      
      return snippet;
    };

    // 高亮搜索词
    const highlightText = (text) => {
      if (!text || !searchQuery.value) return text;
      
      const query = searchQuery.value.trim();
      if (query.length < 2) return text;
      
      const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
      return text.replace(regex, '<mark class="highlight">$1</mark>');
    };

    // 转义正则特殊字符
    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // 清除搜索
    const clearSearch = () => {
      searchQuery.value = '';
      searchMode.value = false;
      searchResults.value = [];
    };

    // 跳转到分类
    const goToCategory = (key) => {
      router.push(`/docs/${key}`);
    };

    onMounted(() => {
      loadConfig();
      loadSearchIndex();
    });

    return {
      config,
      loading,
      searchQuery,
      searchMode,
      searching,
      searchResults,
      getIcon,
      handleSearch,
      clearSearch,
      highlightText,
      goToCategory,
    };
  }
};
</script>

<style scoped>
.docs-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.category-grid {
  margin-top: 24px;
}

.category-card {
  height: 100%;
  transition: all 0.3s;
}

.category-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.category-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-icon {
  font-size: 20px;
  color: #1890ff;
}

.category-desc {
  color: #666;
  margin: 0;
  min-height: 44px;
}

.category-action {
  color: #1890ff;
}

.search-results {
  margin-top: 24px;
}

.search-result-title {
  font-size: 16px;
  font-weight: 500;
}

.search-snippet {
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

:deep(.highlight) {
  background-color: #ffe58f;
  padding: 0 2px;
  border-radius: 2px;
}
</style>

